import SaveAsTemplatePanel from '@/components/templates/SaveAsTemplatePanel';
import TemplateComposerControls from '@/components/templates/TemplateComposerControls';
import { useAuth } from '@/contexts/auth-context';
import { useInvitationStoreSelector } from '@/contexts/invitation-context';
import { useIsBrandManager } from '@/hooks/use-is-brand-manager';
import type { InvitationMethod } from '@/types/invitation';
import type { BatchAttachmentInfo, PayloadLocale } from '@fluenceur/types';
import type { EditorState } from 'lexical';
import { useCallback, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useInvitationTemplateFlow } from './use-invitation-template-flow';

/**
 * Message wiring shared by the two details views (bulk wishlist email and the
 * DM/comment view): the store's editor slices, the language override, the
 * template-library flow (selector + save-as-template, manager-gated) and the
 * editor change handler. Extracted so the split views cannot drift; each view
 * keeps its own body source, validation and send.
 *
 * Distinct from the outreach composer assembly on purpose: these views predate
 * the seed-emission guard (their editors mirror through `handleEditorChange` +
 * explicit init effects) and resolve their editor keys from the TEMPLATE's
 * language, not the selected one — folding them into the assembly would change
 * behavior, not just move code.
 */
export const useDetailsMessageWiring = (
  activeMethod: InvitationMethod,
  onApplyAttachments?: (files: BatchAttachmentInfo[]) => boolean | Promise<boolean>
) => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const messageSnapshot = useInvitationStoreSelector((state) => state.messageSnapshot);
  const compiledMessage = useInvitationStoreSelector((state) => state.compiledMessage);
  const originalTemplate = useInvitationStoreSelector((state) => state.originalTemplate);
  const setMessageSnapshot = useInvitationStoreSelector((state) => state.setMessageSnapshot);
  const setCompiledMessage = useInvitationStoreSelector((state) => state.setCompiledMessage);
  const setOriginalTemplate = useInvitationStoreSelector((state) => state.setOriginalTemplate);
  const resetMessage = useInvitationStoreSelector((state) => state.resetMessage);
  const messageLanguage = useInvitationStoreSelector((state) => state.messageLanguage);
  const setMessageLanguage = useInvitationStoreSelector((state) => state.setMessageLanguage);

  // Brand's default locale (fallback to English when unset). The toggle is ON
  // whenever the brand has explicitly overridden the locale.
  const brandDefaultLanguage: PayloadLocale =
    user?.currentBrand?.settings?.preferredLanguage ?? 'en';
  const languageToggleEnabled = messageLanguage !== undefined;
  const selectedLanguage: PayloadLocale = messageLanguage ?? brandDefaultLanguage;

  // Only push language into the queryKey when it differs from the brand default —
  // otherwise toggling ON without changing the locale would trigger a redundant
  // fetch for the same content under a new cache key.
  const overrideLanguage =
    messageLanguage && messageLanguage !== brandDefaultLanguage ? messageLanguage : undefined;

  // True while the editor body still matches the loaded template (no manual edit).
  const isEditorUntouched = !messageSnapshot || messageSnapshot === originalTemplate;

  // Template library wiring (selector + save-as-template). When the pair has no
  // entries the selector stays inert and the backend-prepared template keeps
  // driving the body (legacy fallback preserved).
  const templateFlow = useInvitationTemplateFlow({
    brandId: user?.currentBrand?.id,
    method: activeMethod,
    language: selectedLanguage,
    isEditorUntouched,
    onApplyAttachments,
  });

  // Saving a template is a management action: owner/admin only. Selecting an
  // existing template (read/use) stays available to anyone who can send. Save
  // only surfaces once the body diverges from the loaded template.
  const { canManage: canManageTemplates } = useIsBrandManager();
  const showSaveTemplate =
    canManageTemplates && (!isEditorUntouched || templateFlow.attachmentsCustomized);

  // Editor footer: the template picker, optionally preceded by the save action.
  // The save seeds from the base body (`messageSnapshot`), so even the bulk
  // branch captures the reusable template, never a per-recipient AI output.
  const buildEditorFooter = (disabled: boolean): ReactNode => (
    <TemplateComposerControls
      entries={templateFlow.entries}
      selectedEntryId={templateFlow.selectedEntryId}
      onSelect={templateFlow.selectEntry}
      disabled={disabled || templateFlow.attachmentsBusy}
      saveDisabled={templateFlow.attachmentsBlocked}
      confirmAttachmentReplacement={templateFlow.attachmentsCustomized}
      isLoading={templateFlow.isLoading}
      showSave={showSaveTemplate}
      onSave={templateFlow.openSaveDrawer}
      saveLabel={t('invitations.modal.saveTemplate.action')}
    />
  );

  // Single overlay per view; anchors to the modal shell content (see the
  // positioning invariant on SaveAsTemplatePanel).
  const saveAsTemplatePanel: ReactNode = templateFlow.saveSeed ? (
    <SaveAsTemplatePanel
      open={templateFlow.isSaveDrawerOpen}
      seed={templateFlow.saveSeed}
      onClose={templateFlow.closeSaveDrawer}
      onSaved={templateFlow.onTemplateSaved}
      showBack
    />
  ) : null;

  const handleEditorChange = useCallback(
    (editorState: EditorState, plainText: string) => {
      const editorStateJson = JSON.stringify(editorState.toJSON());
      setMessageSnapshot(editorStateJson);
      setCompiledMessage(plainText);
    },
    [setMessageSnapshot, setCompiledMessage]
  );

  // Stable handler so the memo on MessageLanguageToggle isn't busted across renders.
  const handleLanguageToggle = useCallback(
    (checked: boolean) => setMessageLanguage(checked ? brandDefaultLanguage : undefined),
    [setMessageLanguage, brandDefaultLanguage]
  );

  return {
    messageSnapshot,
    compiledMessage,
    originalTemplate,
    setMessageSnapshot,
    setOriginalTemplate,
    resetMessage,
    messageLanguage,
    setMessageLanguage,
    brandDefaultLanguage,
    languageToggleEnabled,
    selectedLanguage,
    overrideLanguage,
    isEditorUntouched,
    templateFlow,
    buildEditorFooter,
    saveAsTemplatePanel,
    handleEditorChange,
    handleLanguageToggle,
  };
};
